import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API_URL = window.__BACKEND_URL;

/**
 * Product Skin Context
 * Provides global access to platform branding (logo, symbol, favicon, colors)
 * Fetches from /api/admin/product-skin/public endpoint
 */
const ProductSkinContext = createContext({
  skin: null,
  loading: true,
  refresh: () => {}
});

export function ProductSkinProvider({ children }) {
  const [skin, setSkin] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchSkin = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/product-skin/public`);
      if (res.ok) {
        const data = await res.json();
        setSkin(data);
        
        // Update favicon if set
        if (data.favicon_url) {
          updateFavicon(`${API_URL}${data.favicon_url}`);
        }
      }
    } catch (err) {
      console.error('Failed to fetch product skin:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSkin();
  }, [fetchSkin]);

  return (
    <ProductSkinContext.Provider value={{ skin, loading, refresh: fetchSkin }}>
      {children}
    </ProductSkinContext.Provider>
  );
}

export function useProductSkin() {
  return useContext(ProductSkinContext);
}

/**
 * Update favicon with cache-busting
 */
function updateFavicon(url) {
  // Remove existing favicons
  const existingLinks = document.querySelectorAll("link[rel*='icon']");
  existingLinks.forEach(link => link.remove());

  // Add new favicon
  const link = document.createElement('link');
  link.rel = 'icon';
  link.href = url;
  document.head.appendChild(link);

  // Also add apple-touch-icon
  const appleLink = document.createElement('link');
  appleLink.rel = 'apple-touch-icon';
  appleLink.href = url;
  document.head.appendChild(appleLink);
}

export default ProductSkinContext;
